import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface SchoolCalendarLesson {
  id: string
  studentId: string
  studentName: string | null
  scheduledAt: string
  plannedEndAt: string | null
  finishedAt: string | null
  status: 'scheduled' | 'completed' | 'cancelled'
}

/**
 * Read-only view of one instructor's CONFIRMED lessons, scoped to the school currently
 * selected (via driving_lessons.school_id) -- relies entirely on the pre-existing
 * "driving_lessons: school admin read" RLS policy (migration 092, gated by
 * school_member_can_view_schedules), which already returns only lessons for students
 * assigned to this school. No new RLS, and deliberately no access to
 * lesson_booking_requests (pending negotiation) or the instructor's own availability
 * configuration -- the school sees confirmed bookings only, never anything else on the
 * instructor's calendar, matching the product requirement exactly.
 */
export function useSchoolInstructorCalendar(schoolId: string | null, instructorId: string | null) {
  const [lessons, setLessons] = useState<SchoolCalendarLesson[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLessons = useCallback(async () => {
    if (!schoolId || !instructorId) { setLessons([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const { data: lessonRows, error: lessonErr } = await supabase
        .from('driving_lessons')
        .select('id, student_id, scheduled_at, planned_end_at, finished_at, status')
        .eq('school_id', schoolId)
        .eq('instructor_id', instructorId)
        .neq('status', 'cancelled')
        .order('scheduled_at', { ascending: true })

      if (lessonErr) { setError('Could not load this instructor\'s calendar.'); return }
      if (!lessonRows || lessonRows.length === 0) { setLessons([]); return }

      const studentIds = [...new Set(lessonRows.map(l => l.student_id))]
      const { data: profiles } = await supabase.from('profiles').select('id, full_name').in('id', studentIds)
      const nameById = new Map((profiles ?? []).map(p => [p.id, p.full_name]))

      setLessons(lessonRows.map(l => ({
        id: l.id, studentId: l.student_id, studentName: nameById.get(l.student_id) ?? null,
        scheduledAt: l.scheduled_at, plannedEndAt: l.planned_end_at, finishedAt: l.finished_at,
        status: l.status,
      })))
    } finally {
      setLoading(false)
    }
  }, [schoolId, instructorId])

  useEffect(() => { fetchLessons() }, [fetchLessons])

  return { lessons, loading, error, refresh: fetchLessons }
}
